/**
 * PeriodLockChip - iter971k-H
 * ===========================
 *
 * Small pill shown in the Bank Feed page header (and anywhere else the
 * user is about to edit historical bank data) that surfaces the most
 * recent MANUAL period lock. Because iter971k-G made lock cumulative,
 * showing the latest locked period tells the user *everything up to
 * and including that month is closed*.
 *
 * Data source: GET /api/accounting/periods/latest-lock (falls back
 * silently to null if endpoint is unavailable). Renders nothing when
 * no manual lock exists - zero visual noise for happy-path users.
 *
 * Click behaviour: navigates to Period Close so the user can unlock if
 * needed.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Lock } from 'lucide-react';
import { API_BASE } from '../../utils/apiUrl';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { useNavigate } from 'react-router-dom';

const API = API_BASE;

export const PeriodLockChip = ({ compact = false }) => {
  const [lockedPeriod, setLockedPeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/accounting/periods/latest-lock`);
      // Backend contract: { period: "YYYY-MM" | null, lock_type: "monthly"|"annual" | null }
      const p = res?.data?.period;
      setLockedPeriod(p || null);
    } catch {
      setLockedPeriod(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading || !lockedPeriod) return null;

  const [y, m] = lockedPeriod.split('-');
  const monthName = new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-US', { month: 'short' });
  const label = `Locked through ${monthName} ${y}`;

  const chipCls = compact
    ? 'inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-100 cursor-pointer transition-colors'
    : 'inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-xs font-medium text-amber-800 hover:bg-amber-100 cursor-pointer transition-colors shadow-sm';

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={chipCls}
            onClick={() => navigate(
              /^\d{4}-\d{2}$/.test(lockedPeriod)
                ? `/period-close/${lockedPeriod}`
                : '/period-close'
            )}
            data-testid="period-lock-chip"
          >
            <Lock className={compact ? 'w-3 h-3' : 'w-3.5 h-3.5'} />
            <span>{label}</span>
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-[260px] text-xs">
          <div className="font-semibold mb-0.5">Books are closed through {monthName} {y}.</div>
          <div className="text-muted-foreground">
            All months up to and including this one are locked. Click to open Period Close.
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default PeriodLockChip;
